import type { TimelineItem } from '@/features/timeline';
import { addDays, addYears, differenceInMinutes, endOfDay, intervalToDuration, startOfDay } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CONTAINER_WIDTH,
  PERIOD_ITEM_COLOR,
  POINT_ITEM_COLOR,
  MIN_WIDTH_FOR_TEXT,
  TIMELINE_MAX_SPAN_YEARS,
  TIMELINE_MIN_SPAN_DAYS,
} from '../libs/const';
import { getTimelineConfigByRange, getSpanDaysByLabel, type TimelineScaleLabel } from '../libs/timeline-config';
import { shouldShowPeriod } from '../libs/utils';
import { calculateZoomedRange, clampDateRangeSpan } from '../libs/zoom-range';
import { DateRange, HoverTooltipState, ItemPosition, PeriodSegment, TimelineItemWithDisplay } from './timeline-calendar.types';

interface UseTimelineCalendarInput {
  items: TimelineItem[];
  initialScale?: TimelineScaleLabel;
  initialViewRange?: { start: Date; end: Date };
  onViewChange?: (scale: string, viewRange: { start: Date; end: Date }) => void;
}

interface ItemDateMetrics {
  item: TimelineItem;
  startMs: number;
  endMs: number;
}

const toEpochMs = (value: string): number => {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const computeItemDateRange = (items: TimelineItem[]): { start: Date; end: Date } => {
  if (items.length === 0) {
    const today = startOfDay(new Date());
    const oneMonthLater = addDays(today, 30);
    return { start: today, end: oneMonthLater };
  }

  let minMs = Number.POSITIVE_INFINITY;
  let maxMs = Number.NEGATIVE_INFINITY;
  for (const item of items) {
    const startMs = Date.parse(item.startDate);
    if (Number.isFinite(startMs) && startMs < minMs) {
      minMs = startMs;
    }
    const endMs = item.endDate ? Date.parse(item.endDate) : startMs;
    if (Number.isFinite(endMs) && endMs > maxMs) {
      maxMs = endMs;
    }
  }

  return {
    start: startOfDay(new Date(minMs)),
    end: endOfDay(new Date(maxMs)),
  };
};

const buildInitialViewRange = (
  baseRange: { start: Date; end: Date },
  initialScale?: TimelineScaleLabel,
): DateRange | null => {
  if (!initialScale) {
    return null;
  }

  const centerMs = (baseRange.start.getTime() + baseRange.end.getTime()) / 2;
  const spanDays = getSpanDaysByLabel(initialScale);
  const halfSpanMs = (spanDays * 24 * 60 * 60 * 1000) / 2;

  return {
    start: new Date(centerMs - halfSpanMs),
    end: new Date(centerMs + halfSpanMs),
  };
};

export const useTimelineCalendar = ({ items, initialScale, initialViewRange, onViewChange }: UseTimelineCalendarInput) => {
  const { t } = useTranslation();

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [hoveredTooltip, setHoveredTooltip] = useState<HoverTooltipState | null>(null);

  const itemDateRange = useMemo(() => computeItemDateRange(items), [items]);

  const [viewRange, setViewRange] = useState<DateRange | null>(
    () => initialViewRange ?? buildInitialViewRange(itemDateRange, initialScale),
  );

  const itemDateMetrics = useMemo<ItemDateMetrics[]>(() => {
    return items.map((item) => {
      const startMs = toEpochMs(item.startDate);
      const endMs = item.endDate ? toEpochMs(item.endDate) : startMs;
      return { item, startMs, endMs };
    });
  }, [items]);

  // 全アイテムの日付範囲を計算
  const fullDateRange = useMemo(() => {
    if (itemDateMetrics.length === 0) {
      return itemDateRange;
    }

    let minStartMs = itemDateMetrics[0].startMs;
    let maxEndMs = itemDateMetrics[0].endMs;
    itemDateMetrics.forEach(({ startMs, endMs }) => {
      if (startMs < minStartMs) {
        minStartMs = startMs;
      }
      if (endMs > maxEndMs) {
        maxEndMs = endMs;
      }
    });

    return {
      start: new Date(minStartMs),
      end: new Date(maxEndMs),
    };
  }, [itemDateMetrics, itemDateRange]);

  const defaultViewRange = useMemo(() => {
    return itemDateRange;
  }, [itemDateRange]);

  const minZoomSpanMs = useMemo(() => {
    const base = new Date(2000, 0, 1);
    return addDays(base, TIMELINE_MIN_SPAN_DAYS).getTime() - base.getTime();
  }, []);

  const maxZoomSpanMs = useMemo(() => {
    const base = new Date(2000, 0, 1);
    return addYears(base, TIMELINE_MAX_SPAN_YEARS).getTime() - base.getTime();
  }, []);

  // 実際の表示範囲
  const dateRange = useMemo(() => {
    return viewRange ?? defaultViewRange;
  }, [defaultViewRange, viewRange]);

  const currentConfig = useMemo(() => getTimelineConfigByRange(dateRange), [dateRange]);

  // viewRange 変更を外部に通知（デバウンス）
  const onViewChangeRef = useRef(onViewChange);
  onViewChangeRef.current = onViewChange;

  useEffect(() => {
    if (!onViewChangeRef.current || !viewRange) return;
    const timer = setTimeout(() => {
      onViewChangeRef.current?.(currentConfig.label, viewRange);
    }, 500);
    return () => clearTimeout(timer);
  }, [viewRange, currentConfig.label]);

  // 期間のリストを生成
  const periods = useMemo(() => {
    return currentConfig.getInterval(dateRange.start, dateRange.end);
  }, [dateRange, currentConfig]);

  // 各期間の日数を計算
  const periodDays = useMemo(() => {
    return periods.map((period: Date) => currentConfig.getDayCount(period));
  }, [periods, currentConfig]);

  const totalDays = periodDays.reduce((sum: number, days: number) => sum + days, 0);
  const viewportStartMs = dateRange.start.getTime();
  const viewportEndExclusiveMs = Math.max(dateRange.end.getTime() + 1, viewportStartMs + 1);
  const viewportSpanMs = viewportEndExclusiveMs - viewportStartMs;

  const periodSegments = useMemo<PeriodSegment[]>(() => {
    return periods
      .map((period, index) => {
        const periodStartMs = period.getTime();
        const periodEndExclusive = index < periods.length - 1
          ? periods[index + 1]
          : addDays(period, currentConfig.getDayCount(period));
        const periodEndExclusiveMs = periodEndExclusive.getTime();

        const overlapStartMs = Math.max(periodStartMs, viewportStartMs);
        const overlapEndMs = Math.min(periodEndExclusiveMs, viewportEndExclusiveMs);
        if (overlapEndMs <= overlapStartMs) {
          return null;
        }

        return {
          start: period,
          endExclusive: periodEndExclusive,
          leftPct: ((overlapStartMs - viewportStartMs) / viewportSpanMs) * 100,
          widthPct: ((overlapEndMs - overlapStartMs) / viewportSpanMs) * 100,
          label: currentConfig.formatHeader(period),
          showLabel: shouldShowPeriod(index, periods.length),
        } satisfies PeriodSegment;
      })
      .filter((segment): segment is PeriodSegment => segment !== null);
  }, [currentConfig, periods, viewportEndExclusiveMs, viewportSpanMs, viewportStartMs]);

  const sortedItemMetrics = useMemo(() => {
    return [...itemDateMetrics].sort((a, b) => a.startMs - b.startMs);
  }, [itemDateMetrics]);

  // 開始日順にインデックスを付与
  const itemsWithColors = useMemo(() => {
    return sortedItemMetrics.map(({ item }, index) => ({
      ...item,
      color: item.endDate ? PERIOD_ITEM_COLOR : POINT_ITEM_COLOR,
      index,
    } as TimelineItemWithDisplay));
  }, [sortedItemMetrics]);

  const itemRangeMsById = useMemo(() => {
    const ranges = new Map<string, {
      startDate: string;
      endDate?: string;
      startMs: number;
      endExclusiveMs: number;
    }>();

    const dayMs = 24 * 60 * 60 * 1000;
    sortedItemMetrics.forEach(({ item, startMs, endMs }) => {
      ranges.set(item.id, {
        startDate: item.startDate,
        ...(item.endDate ? { endDate: item.endDate } : {}),
        startMs,
        endExclusiveMs: item.endDate ? endMs + dayMs : endMs,
      });
    });
    return ranges;
  }, [sortedItemMetrics]);

  // アイテムの位置を計算
  const calculateItemPosition = (item: TimelineItem): ItemPosition => {
    const cached = itemRangeMsById.get(item.id);
    const hasSameRange = cached
      && cached.startDate === item.startDate
      && cached.endDate === item.endDate;
    const computeStartMs = (): number => {
      if (hasSameRange) {
        return cached.startMs;
      }
      return new Date(item.startDate).getTime();
    };
    const computeEndExclusiveMs = (): number => {
      if (hasSameRange) {
        return cached.endExclusiveMs;
      }
      const dayMs = 24 * 60 * 60 * 1000;
      if (item.endDate) {
        return new Date(item.endDate).getTime() + dayMs;
      }
      return new Date(item.startDate).getTime();
    };
    const itemStartMs = computeStartMs();
    const itemEndExclusiveMs = computeEndExclusiveMs();

    // 表示範囲外のアイテムの処理
    if (itemEndExclusiveMs <= viewportStartMs || itemStartMs >= viewportEndExclusiveMs) {
      return {
        left: '0%',
        width: '0%',
        isVisible: false,
      };
    }

    // 表示範囲に合わせてクリップ
    const visibleStartMs = Math.max(itemStartMs, viewportStartMs);
    const visibleEndMs = Math.min(itemEndExclusiveMs, viewportEndExclusiveMs);
    const visibleDurationMs = Math.max(visibleEndMs - visibleStartMs, 1);

    const left = ((visibleStartMs - viewportStartMs) / viewportSpanMs) * 100;
    const width = (visibleDurationMs / viewportSpanMs) * 100;
    
    return {
      left: `${left}%`,
      width: `${width}%`,
      isVisible: true,
    };
  };

  // 文字表示の判定
  const shouldShowText = (width: string): boolean => {
    const actualWidth = (parseFloat(width.replace('%', '')) / 100) * CONTAINER_WIDTH;
    return actualWidth >= MIN_WIDTH_FOR_TEXT;
  };

  const handleItemClick = (itemId: string) => {
    setSelectedItemId(itemId);
  };

  const handleItemHover = (hoverState: HoverTooltipState | null) => {
    setHoveredTooltip(hoverState);
  };

  useEffect(() => {
    if (!selectedItemId) {
      return;
    }

    const itemIdSet = new Set(itemsWithColors.map((item) => item.id));
    if (selectedItemId && !itemIdSet.has(selectedItemId)) {
      setSelectedItemId(null);
    }
  }, [itemsWithColors, selectedItemId]);

  const getRelativeFromStartLabel = (startDate: string): string => {
    const today = startOfDay(new Date());
    const target = startOfDay(new Date(startDate));

    if (target.getTime() === today.getTime()) {
      return t('duration.today');
    }

    const isFuture = target > today;
    const from = isFuture ? today : target;
    const to = isFuture ? target : today;
    const duration = intervalToDuration({ start: from, end: to });
    const years = duration.years ?? 0;
    const months = duration.months ?? 0;
    const days = duration.days ?? 0;
    const parts: string[] = [];
    if (years > 0) parts.push(t('duration.year', { count: years }));
    if (months > 0) parts.push(t('duration.month', { count: months }));
    if (days > 0) parts.push(t('duration.day', { count: days }));
    const label = parts.length > 0 ? parts.join('') : t('duration.zeroDays');

    return isFuture ? t('duration.later', { label }) : t('duration.ago', { label });
  };

  const getDurationLabel = (startDate: string, endDate?: string): string | null => {
    if (!endDate) {
      return null;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (end.getTime() <= start.getTime()) {
      return null;
    }

    const totalMinutes = differenceInMinutes(end, start);
    if (totalMinutes <= 3 * 24 * 60) {
      const d = Math.floor(totalMinutes / (24 * 60));
      const h = Math.floor((totalMinutes % (24 * 60)) / 60);
      const m = totalMinutes % 60;
      const parts: string[] = [];
      if (d > 0) parts.push(t('duration.day', { count: d }));
      if (h > 0) parts.push(t('duration.hour', { count: h }));
      if (m > 0) parts.push(t('duration.minute', { count: m }));
      const label = parts.length > 0 ? parts.join('') : t('duration.zeroMinutes');
      return t('duration.span', { label });
    }

    const duration = intervalToDuration({ start, end });
    const years = duration.years ?? 0;
    const months = duration.months ?? 0;
    const days = duration.days ?? 0;
    const parts: string[] = [];
    if (years > 0) parts.push(t('duration.year', { count: years }));
    if (months > 0) parts.push(t('duration.month', { count: months }));
    if (days > 0) parts.push(t('duration.day', { count: days }));
    const label = parts.length > 0 ? parts.join('') : t('duration.zeroDays');

    return t('duration.span', { label });
  };

  // 期間の移動
  const movePeriod = (direction: 'prev' | 'next') => {
    const multiplier = direction === 'prev' ? -1 : 1;
    const spanMs = dateRange.end.getTime() - dateRange.start.getTime();
    const spanYears = Math.round(spanMs / (365.25 * 24 * 60 * 60 * 1000));
    if (spanYears >= 1) {
      const years = spanYears * multiplier;
      setViewRange({
        start: addYears(dateRange.start, years),
        end: addYears(dateRange.end, years),
      });
    } else {
      const shiftMs = Math.max(spanMs + 1, 1) * multiplier;
      setViewRange({
        start: new Date(dateRange.start.getTime() + shiftMs),
        end: new Date(dateRange.end.getTime() + shiftMs),
      });
    }
  };

  const resetToToday = () => {
    const currentRange = viewRange ?? defaultViewRange;
    const spanMs = currentRange.end.getTime() - currentRange.start.getTime();
    const todayMs = startOfDay(new Date()).getTime();
    const halfSpanMs = spanMs / 2;
    setViewRange({
      start: new Date(todayMs - halfSpanMs),
      end: new Date(todayMs + halfSpanMs),
    });
  };

  const setViewRangeClamped = (range: DateRange) => {
    setViewRange(
      clampDateRangeSpan({
        range,
        minSpanMs: minZoomSpanMs,
        maxSpanMs: maxZoomSpanMs,
      }),
    );
  };

  const zoomByScale = (scale: number) => {
    setViewRange((prevRange) => {
      const currentRange = prevRange ?? defaultViewRange;
      return calculateZoomedRange({
        range: currentRange,
        scale,
        minSpanMs: minZoomSpanMs,
        maxSpanMs: maxZoomSpanMs,
      });
    });
  };

  const setScale = (label: TimelineScaleLabel) => {
    const currentRange = viewRange ?? defaultViewRange;
    const centerMs = (currentRange.start.getTime() + currentRange.end.getTime()) / 2;
    const spanDays = getSpanDaysByLabel(label);
    const halfSpanMs = (spanDays * 24 * 60 * 60 * 1000) / 2;
    setViewRangeClamped({
      start: new Date(centerMs - halfSpanMs),
      end: new Date(centerMs + halfSpanMs),
    });
  };

  const panByMs = (deltaMs: number) => {
    if (!Number.isFinite(deltaMs) || deltaMs === 0) {
      return;
    }

    setViewRange((prevRange) => {
      const currentRange = prevRange ?? defaultViewRange;
      return {
        start: new Date(currentRange.start.getTime() + deltaMs),
        end: new Date(currentRange.end.getTime() + deltaMs),
      };
    });
  };

  return {
    selectedItemId,
    hoveredItemId: hoveredTooltip?.itemId ?? null,
    hoveredTooltip,
    dateRange,
    fullDateRange,
    currentConfig,
    periods,
    periodDays,
    totalDays,
    periodSegments,
    itemsWithColors,
    calculateItemPosition,
    shouldShowText,
    handleItemClick,
    handleItemHover,
    getRelativeFromStartLabel,
    getDurationLabel,
    setViewRange,
    setViewRangeClamped,
    setScale,
    zoomByScale,
    panByMs,
    minZoomSpanMs,
    maxZoomSpanMs,
    movePeriod,
    resetToToday,
  };
};
